/*
 * Legacy customer history — read seam + pure formatters (WU
 * PROJ-0062-WU-LEGACY-HISTORY-01; LOCKED PROGRAMME-DEC-LEGACY-CUSTOMER-
 * MIGRATION-V1 sinks 2/5).
 *
 * VISIBILITY (all must hold, else render NOTHING — never an error UI):
 *   flag VITE_LEGACY_HISTORY_ENABLED === 'true' AND a session + my-api base AND
 *   a 200 well-formed GET /me/history AND legacy_status === 'linked' AND
 *   legacy !== null.
 *
 * MONEY (ruling 1): gross receipts per currency, refunds NOT netted — labelled
 * "Total paid", never "net". Each currency is its own line: never converted,
 * never summed across currencies. Minor → major via the currency's own
 * fraction digits as Intl reports them (GBP 2, JPY 0, KWD 3 …).
 * CONVERTED TOTAL (schema v2, contract amendment 02): under the per-currency
 * lines, ONE approximate line "Total ≈ 88.52 USD" in the server's
 * display_currency, converted at the rate on each purchase date — shown only
 * when the server sends converted totals and there is priced spend.
 */
import { useEffect, useState } from 'react'
import { loadSession } from '../auth/session'
import { legacyHistoryEnabled } from '../lib/legacyHistoryFlag'
import type { Lang } from '../i18n/useLocale'
import { fetchMyHistory, myApiBase, type MyHistory, type MyLegacyHistory } from './myApi'

/** The legacy block to render, or null (not linked / unknown / absent). */
export function visibleLegacyHistory(h: MyHistory | null): MyLegacyHistory | null {
  return h && h.legacy_status === 'linked' ? h.legacy : null
}

/** Formatting locale — mirrors the app's existing convention
 *  (EligibilityMismatchNotice: 'ar' under Arabic, else 'en-GB'). */
const localeFor = (lang: Lang): string => (lang === 'ar' ? 'ar' : 'en-GB')

/** 123450, 'GBP' → '£1,234.50'; 45000, 'AED' → 'AED 450.00'. */
export function formatMinorAmount(minor: number, currency: string, lang: Lang): string {
  try {
    const nf = new Intl.NumberFormat(localeFor(lang), { style: 'currency', currency })
    const digits = nf.resolvedOptions().maximumFractionDigits ?? 2
    return nf.format(minor / 10 ** digits)
  } catch {
    // Unknown code to this runtime's Intl — show the honest raw value.
    return `${(minor / 100).toFixed(2)} ${currency}`
  }
}

/** The approximate converted total line's amount (schema v2): minor units of
 *  one of the six targets (all exponent 2) as plain digits plus the ISO code,
 *  e.g. 8852, 'USD' → '88.52 USD'. The code is spelled out (not a symbol) so
 *  '$' can never be read as another dollar. */
export function formatConvertedTotal(minor: number, currency: string, lang: Lang): string {
  const n = new Intl.NumberFormat(localeFor(lang), { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(minor / 100)
  return `${n} ${currency}`
}

/** The converted total to show, or null (hide the line): only when the server
 *  sent a converted block (never partial) AND the user has priced spend. */
export function convertedTotalToShow(legacy: MyLegacyHistory): { minor: number; currency: string } | null {
  if (!legacy.converted || !legacy.display_currency || legacy.totals_by_currency.length === 0) return null
  const minor = legacy.converted[legacy.display_currency]
  return typeof minor === 'number' ? { minor, currency: legacy.display_currency } : null
}

/** 'YYYY-MM-DD' as a PLAIN calendar date — built and formatted in UTC so no
 *  viewer timezone can shift it a day. '2019-03-14' → '14 Mar 2019'. */
export function formatPlainDate(ymd: string, lang: Lang): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd)
  if (!m) return ymd
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  return d.toLocaleDateString(localeFor(lang), { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
}

/** Read the caller's legacy history once per mount. null until (and unless) a
 *  visible linked block arrives; flag off ⇒ no request at all. */
export function useLegacyHistory(env: Record<string, string | undefined> = import.meta.env): MyLegacyHistory | null {
  const enabled = legacyHistoryEnabled(env)
  const base = myApiBase(env)
  const [legacy, setLegacy] = useState<MyLegacyHistory | null>(null)
  useEffect(() => {
    if (!enabled || !base) return
    const session = loadSession()
    if (!session) return
    let cancelled = false
    void fetchMyHistory(session.idToken, base).then((h) => {
      if (!cancelled) setLegacy(visibleLegacyHistory(h))
    })
    return () => { cancelled = true }
  }, [enabled, base])
  return enabled ? legacy : null
}

/** A finish time in ms as h:mm:ss (whole seconds, truncated — a result is
 *  never rounded up). 2_715_400 → '0:45:15'; 36_000_000 → '10:00:00'. */
export function formatFinishTime(ms: number): string {
  const total = Math.floor(ms / 1000)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}
